import React from 'react';
import { Modal, Spin } from 'antd';
import EligibilityStatusTag from 'components/EligibilityStatusTag';
import { TEligibilityCheckerRole, TEligibilityCheckHistoryRow } from 'types/insuranceEligibility';
import { formatHistoryDate, historyRowTag } from '../derive';
import { HISTORY_COPY } from '../copy';
import s from '../s.module.less';

type Props = {
    open: boolean;
    loading: boolean;
    loadingMore: boolean;
    rows?: TEligibilityCheckHistoryRow[];
    hasMore: boolean;
    // 结果卡上那一次的时间戳：流水里对应的行标 Active（后端不下发这个标记）
    currentCheckedAt?: string;
    // solo 练所没有第二个人：角色角标与 "as" 次行都不印
    showWho: boolean;
    onLoadMore: () => void;
    onClose: () => void;
};

// role.name → 徽章文字（后端 §2.8）。Owner 琥珀、Provider 紫，其余助理类与 System 一律灰：
// 这一列要回答的是「这次是谁跑的」，助理之间的头衔差别不值得三种颜色
// role.name → 徽章文字（后端 §2.8）。后端把助理拆成 practice manager / administrative /
// clinical 三档，这里统一显示 Admin——这一列回答的是「谁跑的」，三个头衔的差别不值得把
// Checked by 撑宽（展示决定，不是漏改）。Owner 琥珀、Provider 紫、其余灰
const ROLE_LABEL: Record<TEligibilityCheckerRole, string> = {
    GROUP_OWNER: 'Owner',
    GROUP_PROVIDER: 'Provider',
    GROUP_PRACTICE_MANAGER: 'Admin',
    GROUP_ADMINISTRATIVE_ASSISTANT: 'Admin',
    GROUP_CLINICAL_ASSISTANT: 'Admin',
    SYSTEM: 'System',
};

// 滚到离底多少像素开始取下一页：一屏 20 行，提前一点点避免滚到尽头等白屏
const LOAD_MORE_THRESHOLD = 48;

// (当前 provider, 当前 patient) 这一对的检查流水（原型 09/15 #historyOverlay）。
// 按钮钉在标题行而不是结果卡的 bar 上：它讲的是这条记录，挂在卡上会被读成「这一份 plan 的历史」。
// 没有 footer——X 关掉就行，这里没有要确认的东西
const HistoryModal: React.FC<Props> = ({
    open,
    loading,
    loadingMore,
    rows,
    hasMore,
    currentCheckedAt,
    showWho,
    onLoadMore,
    onClose,
}) => {
    const list = rows || [];

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (!hasMore || loadingMore) return;
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight <= LOAD_MORE_THRESHOLD) onLoadMore();
    };

    let body: React.ReactNode;
    if (loading) {
        body = (
            <div className={s.histLoading}>
                <Spin />
            </div>
        );
    } else if (!list.length) {
        body = <p className={s.histEmpty}>{HISTORY_COPY.empty}</p>;
    } else {
        body = (
            <div className={s.histScroll} onScroll={handleScroll}>
                <table className={s.histTable}>
                    <thead>
                        <tr>
                            <th>{HISTORY_COPY.columns.plan}</th>
                            <th>{HISTORY_COPY.columns.insuranceType}</th>
                            <th>{HISTORY_COPY.columns.result}</th>
                            <th>{HISTORY_COPY.columns.date}</th>
                            {showWho && <th>{HISTORY_COPY.columns.by}</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((row) => {
                            // 按下按钮的人和这次 check 用谁的身份发出是两个事实，只在不同的时候都印：
                            // provider 自查两者同人（后端 §2.8 的判定就是 checkerRole != GROUP_PROVIDER），
                            // 代查（owner / 助理 / 系统）才印第二行
                            const ranAs =
                                showWho &&
                                row.checkerRole !== 'GROUP_PROVIDER' &&
                                row.targetProviderName &&
                                row.targetProviderName !== row.checkerName &&
                                row.targetProviderName;
                            return (
                                <tr key={row.id}>
                                    <td>
                                        <span className={s.histPlan}>{row.carrier || '-'}</span>
                                        {!!currentCheckedAt && row.checkedAt === currentCheckedAt && (
                                            <span className={s.histNow}>{HISTORY_COPY.current}</span>
                                        )}
                                        {row.plan && <span className={s.histSub}>{row.plan}</span>}
                                    </td>
                                    <td className={s.histMuted}>
                                        {row.insuranceCardType
                                            ? HISTORY_COPY.insuranceType[row.insuranceCardType]
                                            : '—'}
                                    </td>
                                    <td>
                                        {/* 词汇表与列表页/发票明细共用；tip 在这张密表里是噪音，关掉。
                                            后端没给 status（result 行写失败）时留破折号，不替 payer 编一个状态 */}
                                        {row.status ? (
                                            <EligibilityStatusTag status={historyRowTag(row.status)} showTip={false} />
                                        ) : (
                                            <span className={s.histMuted}>—</span>
                                        )}
                                    </td>
                                    <td className={s.histMuted}>
                                        {row.checkedAt ? formatHistoryDate(row.checkedAt) : '—'}
                                    </td>
                                    {showWho && (
                                        <td className={s.histMuted}>
                                            {/* SYSTEM 触发没有操作人，后端也不给名字——只留角色角标 */}
                                            {row.checkerName || (row.checkerRole === 'SYSTEM' ? '' : '-')}
                                            {row.checkerRole && (
                                                <span
                                                    className={`${s.histRole} ${
                                                        row.checkerRole === 'GROUP_OWNER' ? s.histRoleOwner : ''
                                                    } ${row.checkerRole === 'GROUP_PROVIDER' ? s.histRoleProvider : ''}`}
                                                >
                                                    {ROLE_LABEL[row.checkerRole]}
                                                </span>
                                            )}
                                            {ranAs && <span className={s.histSub}>{HISTORY_COPY.ranAs(ranAs)}</span>}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {loadingMore && (
                    <div className={s.histMore}>
                        <Spin size="small" />
                    </div>
                )}
            </div>
        );
    }

    return (
        <Modal
            open={open}
            onCancel={onClose}
            title={HISTORY_COPY.title}
            // 10/21 加了 Insurance type 列：原型 760（含 Checked by）；solo 少一列收窄
            width={showWho ? 760 : 640}
            wrapClassName={s.eligModalScope}
            footer={null}
            destroyOnClose
        >
            {body}
        </Modal>
    );
};

export default HistoryModal;
